import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { PagedResponse, SearchParams } from './types';

// --- Tenant Document ---

export interface Tenant {
  id: string;
  name: string;
  domain: string;
  plan: string; // free, starter, professional, enterprise
  status: string; // active, suspended, cancelled
  logo_url: string;
  locale: string;
  contact_email: string;
  contact_phone: string;
  max_users: number;
  address: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

// --- System Setting Document ---

export interface SystemSetting {
  id: string;
  key: string;
  value: string;
  category: string; // general, email, security, appearance
  description: string;
  value_type: string; // string, number, boolean, json
  is_public: boolean;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Tenant Settings Document ---

export interface TenantSettings {
  id: string;
  tenant_id: string;
  key: string;
  value: string;
  category: string; // general, email, notification, appearance
  description: string;
  value_type: string; // string, number, boolean, json
  is_overridden: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Request Types ---

export interface CreateTenantRequest {
  name: string;
  domain: string;
  plan?: string;
  locale?: string;
  contact_email?: string;
  contact_phone?: string;
  max_users?: number;
  address?: string;
  notes?: string;
}

export interface UpdateTenantRequest {
  name: string;
  domain: string;
  plan?: string;
  status?: string;
  logo_url?: string;
  locale?: string;
  contact_email?: string;
  contact_phone?: string;
  max_users?: number;
  address?: string;
  notes?: string;
}

export interface CreateSystemSettingRequest {
  key: string;
  value: string;
  category?: string;
  description?: string;
  value_type?: string;
  is_public?: boolean;
}

export interface UpdateSystemSettingRequest {
  value: string;
  category?: string;
  description?: string;
  value_type?: string;
  is_public?: boolean;
}

export interface CreateTenantSettingsRequest {
  key: string;
  value: string;
  category?: string;
  description?: string;
  value_type?: string;
}

export interface UpdateTenantSettingsRequest {
  value: string;
  category?: string;
  description?: string;
  value_type?: string;
}

// --- Filter Types ---

export interface TenantFilters extends SearchParams {
  status?: string;
  plan?: string;
}

export interface SystemSettingFilters extends SearchParams {
  category?: string;
}

export interface TenantSettingsFilters extends SearchParams {
  category?: string;
}

// --- Tenant API ---

export const getTenants = (params: TenantFilters = {}) =>
  apiGet<PagedResponse<Tenant>>('/api/v1/tenants', params);

export const getTenant = (id: string) =>
  apiGet<Tenant>(`/api/v1/tenants/${id}`);

export const createTenant = (data: CreateTenantRequest) =>
  apiPost<Tenant>('/api/v1/tenants', data);

export const updateTenant = (id: string, data: UpdateTenantRequest) =>
  apiPut<Tenant>(`/api/v1/tenants/${id}`, data);

export const deleteTenant = (id: string) =>
  apiDelete<void>(`/api/v1/tenants/${id}`);

// --- System Setting API ---

export const getSystemSettings = (params: SystemSettingFilters = {}) =>
  apiGet<PagedResponse<SystemSetting>>('/api/v1/system-settings', params);

export const getSystemSetting = (id: string) =>
  apiGet<SystemSetting>(`/api/v1/system-settings/${id}`);

export const createSystemSetting = (data: CreateSystemSettingRequest) =>
  apiPost<SystemSetting>('/api/v1/system-settings', data);

export const updateSystemSetting = (id: string, data: UpdateSystemSettingRequest) =>
  apiPut<SystemSetting>(`/api/v1/system-settings/${id}`, data);

export const deleteSystemSetting = (id: string) =>
  apiDelete<void>(`/api/v1/system-settings/${id}`);

// --- Tenant Settings API ---

export const getTenantSettings = (params: TenantSettingsFilters = {}) =>
  apiGet<PagedResponse<TenantSettings>>('/api/v1/tenant-settings', params);

export const getTenantSetting = (id: string) =>
  apiGet<TenantSettings>(`/api/v1/tenant-settings/${id}`);

export const createTenantSetting = (data: CreateTenantSettingsRequest) =>
  apiPost<TenantSettings>('/api/v1/tenant-settings', data);

export const updateTenantSetting = (id: string, data: UpdateTenantSettingsRequest) =>
  apiPut<TenantSettings>(`/api/v1/tenant-settings/${id}`, data);

export const deleteTenantSetting = (id: string) =>
  apiDelete<void>(`/api/v1/tenant-settings/${id}`);

// --- Notification Preference Document ---

export interface NotificationPreference {
  id: string;
  tenant_id: string;
  user_id: string;
  channel: string; // email, in_app, sms, push
  event_type: string;
  is_enabled: boolean;
  frequency: string; // immediate, daily_digest, weekly_digest
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateNotificationPreferenceRequest {
  user_id: string;
  channel: string;
  event_type: string;
  is_enabled?: boolean;
  frequency?: string;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
}

export interface UpdateNotificationPreferenceRequest {
  is_enabled: boolean;
  frequency?: string;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
}

export interface NotificationPreferenceFilters extends SearchParams {
  user_id?: string;
  channel?: string;
  event_type?: string;
}

// --- Admin Dashboard Summary ---

export interface AdminDashboardSummary {
  total_tenants: number;
  active_tenants: number;
  suspended_tenants: number;
  total_users: number;
  active_subscriptions: number;
  total_revenue: number;
  tenants_by_plan: Array<{ key: string; doc_count: number; value?: number }>;
  tenants_by_status: Array<{ key: string; doc_count: number; value?: number }>;
  new_tenants_trend: Array<{ date: string; doc_count: number; value?: number }>;
  recent_audit_activity: number;
}

// --- Notification Preference API ---

export const getNotificationPreferences = (params: NotificationPreferenceFilters = {}) =>
  apiGet<PagedResponse<NotificationPreference>>('/api/v1/notification-preferences', params);

export const getNotificationPreference = (id: string) =>
  apiGet<NotificationPreference>(`/api/v1/notification-preferences/${id}`);

export const createNotificationPreference = (data: CreateNotificationPreferenceRequest) =>
  apiPost<NotificationPreference>('/api/v1/notification-preferences', data);

export const updateNotificationPreference = (id: string, data: UpdateNotificationPreferenceRequest) =>
  apiPut<NotificationPreference>(`/api/v1/notification-preferences/${id}`, data);

export const deleteNotificationPreference = (id: string) =>
  apiDelete<void>(`/api/v1/notification-preferences/${id}`);

// --- Admin Dashboard API ---

export interface AdminDashboardParams {
  trend_months?: number;
}

export const getAdminDashboardSummary = (params: AdminDashboardParams = {}) =>
  apiGet<AdminDashboardSummary>('/api/v1/admin-dashboard/summary', params);

// --- Tenant Branding ---

export interface TenantBranding {
  id: string;
  tenant_id: string;
  logo_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  company_name: string;
  tagline: string | null;
  footer_text: string | null;
  login_background_url: string | null;
  custom_css: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateTenantBrandingRequest {
  logo_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  company_name: string;
  tagline?: string;
  footer_text?: string;
  login_background_url?: string;
  custom_css?: string;
}

export interface UpdateTenantBrandingRequest {
  logo_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  company_name?: string;
  tagline?: string | null;
  footer_text?: string | null;
  login_background_url?: string | null;
  custom_css?: string | null;
}

export const getTenantBrandings = (params: SearchParams = {}) =>
  apiGet<PagedResponse<TenantBranding>>('/api/v1/tenant-branding', params);

export const getTenantBranding = (id: string) =>
  apiGet<TenantBranding>(`/api/v1/tenant-branding/${id}`);

export const createTenantBranding = (data: CreateTenantBrandingRequest) =>
  apiPost<TenantBranding>('/api/v1/tenant-branding', data);

export const updateTenantBranding = (id: string, data: UpdateTenantBrandingRequest) =>
  apiPut<TenantBranding>(`/api/v1/tenant-branding/${id}`, data);

export const deleteTenantBranding = (id: string) =>
  apiDelete<void>(`/api/v1/tenant-branding/${id}`);

// --- Tenant Domain ---

export interface TenantDomain {
  id: string;
  tenant_id: string;
  domain: string;
  is_primary: boolean;
  status: string; // pending, verified, failed
  verification_method: string; // dns_txt, dns_cname
  verification_token: string;
  verified_at: string | null;
  ssl_status: string; // pending, active, expired, error
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateTenantDomainRequest {
  domain: string;
  is_primary?: boolean;
  verification_method?: string;
}

export interface UpdateTenantDomainRequest {
  is_primary?: boolean;
  verification_method?: string;
}

export interface TenantDomainFilters extends SearchParams {
  status?: string;
}

export const getTenantDomains = (params: TenantDomainFilters = {}) =>
  apiGet<PagedResponse<TenantDomain>>('/api/v1/tenant-domains', params);

export const getTenantDomain = (id: string) =>
  apiGet<TenantDomain>(`/api/v1/tenant-domains/${id}`);

export const createTenantDomain = (data: CreateTenantDomainRequest) =>
  apiPost<TenantDomain>('/api/v1/tenant-domains', data);

export const updateTenantDomain = (id: string, data: UpdateTenantDomainRequest) =>
  apiPut<TenantDomain>(`/api/v1/tenant-domains/${id}`, data);

export const deleteTenantDomain = (id: string) =>
  apiDelete<void>(`/api/v1/tenant-domains/${id}`);

export const verifyTenantDomain = (id: string) =>
  apiPost<TenantDomain>(`/api/v1/tenant-domains/${id}/verify`, {});

// --- Email Template ---

export interface EmailTemplate {
  id: string;
  tenant_id: string;
  name: string;
  type: string; // welcome, reset_password, invoice, notification, ticket_update, campaign
  subject: string;
  body_html: string;
  body_text: string | null;
  description: string | null;
  is_active: boolean;
  variables: string[];
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateEmailTemplateRequest {
  name: string;
  type: string;
  subject: string;
  body_html: string;
  body_text?: string;
  description?: string;
  is_active?: boolean;
  variables?: string[];
}

export interface UpdateEmailTemplateRequest {
  name?: string;
  subject?: string;
  body_html?: string;
  body_text?: string | null;
  description?: string | null;
  is_active?: boolean;
  variables?: string[];
}

export interface EmailTemplateFilters extends SearchParams {
  type?: string;
  is_active?: boolean;
}

export const getEmailTemplates = (params: EmailTemplateFilters = {}) =>
  apiGet<PagedResponse<EmailTemplate>>('/api/v1/email-templates', params);

export const getEmailTemplate = (id: string) =>
  apiGet<EmailTemplate>(`/api/v1/email-templates/${id}`);

export const createEmailTemplate = (data: CreateEmailTemplateRequest) =>
  apiPost<EmailTemplate>('/api/v1/email-templates', data);

export const updateEmailTemplate = (id: string, data: UpdateEmailTemplateRequest) =>
  apiPut<EmailTemplate>(`/api/v1/email-templates/${id}`, data);

export const deleteEmailTemplate = (id: string) =>
  apiDelete<void>(`/api/v1/email-templates/${id}`);
